import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators';
import { isDefined, undefinedIfEmpty } from '../helpers/utils';

import { GetPointResponse } from '../types';
import { StateController } from '../helpers/state/state-management/state-controller';
import { StaticMapLayout } from '../components/static-map-component';
import accountInfo from '../helpers/state/account-info';
import challengeData from '../helpers/state/challenge-data';
import { getMainPage } from '../helpers/page-name';
import { getQueryStringParameters } from '../helpers/urls';
import pointCache from '../helpers/state/point-cache';
import topicData from '../helpers/state/topic-data';

/**
 * Static Map Embed Page
 *
 * A dedicated page for embedding static maps with URL parameters.
 *
 * URL Parameters:
 * - topic_id: The topic/map ID (required)
 * - topic_key: The topic access key (optional)
 * - point_id: Single point ID to display
 * - point_ids: Comma-separated list of point IDs to display
 * - layout: Layout option (map-left-info-right, map-right-info-left, map-top-info-bottom, map-bottom-info-top, map-only)
 * - width: Map width in pixels (default: 600)
 * - height: Map height in pixels (default: 400)
 * - zoom: Zoom level 1-20 (optional, auto-calculated if not provided)
 * - center_lat: Center latitude (optional, auto-calculated if not provided)
 * - center_lng: Center longitude (optional, auto-calculated if not provided)
 * - show_all: Show all points from the map (true/false)
 * - style: Map style name (standard, satellite, hybrid, blue, muted, paper, bright, sketch)
 *
 * Example URLs:
 * /#static-map-embed?topic_id=abc123&point_id=xyz789
 * /#static-map-embed?topic_id=abc123&point_ids=xyz789,def456,ghi123&layout=map-top-info-bottom
 * /#static-map-embed?topic_id=abc123&show_all=true&layout=map-only&width=800&height=600
 */
@customElement('static-map-embed-page')
export class StaticMapEmbedPage extends LitElement {
    amplitude_callsite: string = 'Static Map Embed Page';



    @state()
    private pointId?: string;

    @state()
    private pointIds?: string[];

    @state()
    private readonly points?: GetPointResponse[];

    @state()
    private layout: StaticMapLayout = StaticMapLayout.MapLeftInfoRight;

    @state()
    private mapWidth: number = 600;

    @state()
    private mapHeight: number = 400;

    @state()
    private zoom?: number;

    @state()
    private centerLat?: number;

    @state()
    private centerLng?: number;

    @state()
    private showAllPoints: boolean = false;

    @state()
    private mapStyleName?: string;

    @state()
    private isReady: boolean = false;

    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindPointCacheState = new StateController(this, pointCache);
    private readonly bindAccountState = new StateController(this, accountInfo);

    async firstUpdated() {
        await this.startup();
    }

    private async startup() {
        const queryString = getQueryStringParameters();

        // Get topic info
        const topic_id = queryString.get('topic_id') ?? undefined;
        const topic_key = queryString.get('topic_key') ?? undefined;
        let local_storage_password: string | undefined;
        if (topic_id) {
            local_storage_password = undefinedIfEmpty(localStorage.getItem(`${topic_id}_password`)) ?? topic_key;
        }

        // Initialize topic data
        await topicData.instantiateTopicData(
            this.amplitude_callsite,
            accountInfo.isAuthenticated,
            false,
            true,
            topic_id,
            local_storage_password,
            true
        );

        if (!isDefined(topicData.topic)) {
            getMainPage().errorToastElement?.showToast('Could not load topic data.');
            return;
        }

        // Initialize point cache
        await pointCache.instantiatePointCache(
            topicData.topic,
            topicData.topicKey,
            false,
            true,
            challengeData.allCheckins
        );

        // Parse URL parameters
        this.parseUrlParameters(queryString);

        this.isReady = true;
        window.dispatchEvent(new CustomEvent('page-content-ready'));
    }

    private parseUrlParameters(queryStrings: Map<string, string>) {
        // Point selection
        const point_id = queryStrings.get('point_id');
        const point_ids_str = queryStrings.get('point_ids');
        const show_all = queryStrings.get('show_all');
        if (point_id) {
            this.pointId = point_id;
        } else if (point_ids_str) {
            this.pointIds = point_ids_str.split(',').map(id => id.trim());
        } else if (show_all === 'true') {
            this.showAllPoints = true;
        }

        // Layout
        const layout = queryStrings.get('layout');
        if (layout) {
            switch (layout) {
                case 'map-left-info-right':
                    this.layout = StaticMapLayout.MapLeftInfoRight;
                    break;
                case 'map-right-info-left':
                    this.layout = StaticMapLayout.MapRightInfoLeft;
                    break;
                case 'map-top-info-bottom':
                    this.layout = StaticMapLayout.MapTopInfoBottom;
                    break;
                case 'map-bottom-info-top':
                    this.layout = StaticMapLayout.MapBottomInfoTop;
                    break;
                case 'map-only':
                    this.layout = StaticMapLayout.MapOnly;
                    break;
                case 'card-only':
                    this.layout = StaticMapLayout.CardOnly;
                    break;
            }
        }

        // Dimensions
        const width = queryStrings.get('width');
        const height = queryStrings.get('height');
        if (width) {
            const parsedWidth = parseInt(width, 10);
            if (!isNaN(parsedWidth) && parsedWidth > 0) {
                this.mapWidth = parsedWidth;
            }
        }
        if (height) {
            const parsedHeight = parseInt(height, 10);
            if (!isNaN(parsedHeight) && parsedHeight > 0) {
                this.mapHeight = parsedHeight;
            }
        }

        // Zoom and center
        const zoom = queryStrings.get('zoom');
        const center_lat = queryStrings.get('center_lat');
        const center_lng = queryStrings.get('center_lng');
        if (zoom) {
            const parsedZoom = parseInt(zoom, 10);
            if (!isNaN(parsedZoom) && parsedZoom >= 1 && parsedZoom <= 20) {
                this.zoom = parsedZoom;
            }
        }
        if (center_lat) {
            const parsedLat = parseFloat(center_lat);
            if (!isNaN(parsedLat) && parsedLat >= -90 && parsedLat <= 90) {
                this.centerLat = parsedLat;
            }
        }
        if (center_lng) {
            const parsedLng = parseFloat(center_lng);
            if (!isNaN(parsedLng) && parsedLng >= -180 && parsedLng <= 180) {
                this.centerLng = parsedLng;
            }
        }

        // Style
        const style = queryStrings.get('style');
        if (style) {
            this.mapStyleName = style;
        }
    }

    /**
     * Generate embed code for users
     */
    private getEmbedCode(): string {
        const currentUrl = new URL(window.location.href);
        const embedUrl = currentUrl.toString();
        return `<iframe src="${embedUrl}" width="${this.mapWidth}" height="${this.mapHeight}" style="border:0" allowfullscreen></iframe>`;
    }

    /**
     * Copy embed code to clipboard
     */
    private async copyEmbedCode() {
        const embedCode = this.getEmbedCode();
        try {
            await navigator.clipboard.writeText(embedCode);
            getMainPage().errorToastElement?.showToast('Embed code copied to clipboard!');
        } catch (err) {
            console.error('Failed to copy embed code:', err);
            getMainPage().errorToastElement?.showToast('Failed to copy embed code');
        }
    }

    render() {
        if (!this.isReady || !topicData.topic) {
            return html`
                <div class="loading-container">
                    <loading-element loadingString="Loading map..." ?horizontal=${true}></loading-element>
                </div>
            `;
        }

        return html`
            <div class="embed-page-container">
                ${topicData.isWriter ? html`
                    <div class="embed-controls">
                        <div class="embed-header">
                            <h3>Static Map Embed</h3>
                            <p>Use this page to embed your map as a static image</p>
                        </div>
                        <div class="embed-actions">
                            <form-button
                                size="sm"
                                @click="${this.copyEmbedCode}"
                            >
                                <mwc-icon>content_copy</mwc-icon>
                                Copy Embed Code
                            </form-button>
                            <a
                                href="${window.location.href}"
                                target="_blank"
                                class="preview-link"
                            >
                                <form-button size="sm" variant="outline">
                                    <mwc-icon>open_in_new</mwc-icon>
                                    Preview in New Tab
                                </form-button>
                            </a>
                        </div>
                        <div class="embed-code-display">
                            <code>${this.getEmbedCode()}</code>
                        </div>
                    </div>
                ` : ''}

                <div class="map-container">
                    <static-map-component
                        .pointId=${this.pointId}
                        .pointIds=${this.pointIds}
                        .points=${this.points}
                        .layout=${this.layout}
                        .mapWidth=${this.mapWidth}
                        .mapHeight=${this.mapHeight}
                        .zoom=${this.zoom}
                        .centerLat=${this.centerLat}
                        .centerLng=${this.centerLng}
                        .showAllPoints=${this.showAllPoints}
                        .mapStyleName=${this.mapStyleName}
                    ></static-map-component>
                </div>
            </div>

            <error-toast id="errorToast"></error-toast>
        `;
    }

    static get styles() {
        return css`
            :host {
                display: block;
                width: 100%;
                height: 100%;
            }

            .loading-container {
                display: flex;
                justify-content: center;
                align-items: center;
                min-height: 400px;
                background: var(--theme-light-scrollbar-color, #f5f5f5);
            }

            .embed-page-container {
                display: flex;
                flex-direction: column;
                width: 100%;
                height: 100%;
                background: var(--theme-white-color, #fff);
            }

            .embed-controls {
                padding: 20px;
                background: var(--theme-light-scrollbar-color, #f5f5f5);
                border-bottom: 1px solid var(--theme-border-color, #e0e0e0);
            }

            .embed-header h3 {
                margin: 0 0 8px 0;
                font-size: 18px;
                color: var(--main-primary-color, #102C3D);
            }

            .embed-header p {
                margin: 0 0 16px 0;
                font-size: 14px;
                color: var(--theme-lighter-text-color, #666);
            }

            .embed-actions {
                display: flex;
                gap: 12px;
                margin-bottom: 16px;
                flex-wrap: wrap;
            }

            .preview-link {
                text-decoration: none;
            }

            .embed-code-display {
                background: var(--theme-white-color, #fff);
                border: 1px solid var(--theme-border-color, #e0e0e0);
                border-radius: 4px;
                padding: 12px;
                overflow-x: auto;
            }

            .embed-code-display code {
                font-family: 'Courier New', monospace;
                font-size: 12px;
                color: var(--theme-input-color, #333);
                word-break: break-all;
                white-space: pre-wrap;
            }

            .map-container {
                flex: 1;
                display: flex;
                justify-content: center;
                align-items: flex-start;
                padding: 5px;
                overflow: auto;
            }

            /* Hide controls for non-writers (embedded view) */
            :host(:not([writer])) .embed-controls {
                display: none;
            }

            @media only screen and (max-width: 768px) {
                .embed-controls {
                    padding: 16px;
                }

                .embed-header h3 {
                    font-size: 16px;
                }

                .embed-actions {
                    flex-direction: column;
                }

                .map-container {
                    padding: 12px;
                }
            }
        `;
    }
}
